import { UserDtoFromJwt, UserRights } from '@src/types/User'
import axios, { AxiosError } from 'axios'

export const JWT_LOCAL_STORAGE_KEY = 'merchantToken'
export const REFRESHED_TOKEN_HEADER = 'x-refreshed-token'

export function isExpiredToken(expiryDate: number): boolean {
  const date = new Date().valueOf() / 1000
  return date > expiryDate
}

function parseRights(rightsCsv: string | undefined): UserRights {
  const granted = new Set(
    (rightsCsv ?? '').split(',').map((r) => r.trim()).filter(Boolean),
  )
  return {
    canAddUser: granted.has('canAddUser'),
    canEditUser: granted.has('canEditUser'),
    canSetReportProperties: granted.has('canSetReportProperties'),
  }
}

export function parseJwt(token: string): object {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
      })
      .join(''),
  )

  const payload = JSON.parse(jsonPayload) as { isBannerUser?: string | boolean; rights?: string | UserRights }

  if (payload.isBannerUser !== undefined) {
    payload.isBannerUser = payload.isBannerUser === 'True'
  }

  if (typeof payload.rights === 'string') {
    payload.rights = parseRights(payload.rights)
  }

  return payload
}

export function loadUserFromJwtString(jwtToken: string): UserDtoFromJwt | null {
  const dto = parseJwt(jwtToken) as UserDtoFromJwt
  return isExpiredToken(dto.exp) ? null : dto
}

export function getMerchantAuthToken() {
  let token = localStorage.getItem(JWT_LOCAL_STORAGE_KEY) ?? ''
  const user = token ? loadUserFromJwtString(token) : null
  if (!user) token = ''
  return token
}

function resolveApiUrl() {
  if (import.meta.env.PROD) return `${window.location.origin}/api`
  return import.meta.env.VITE_API_URL
}

function createInstance() {
  const client = axios.create({
    baseURL: resolveApiUrl(),
    headers: {
      'X-requestor': `MerchantsDashboardApp:${import.meta.env.VITE_VERSION}`,
    },
  })

  // Always pull the current token from localStorage so a sliding refresh
  // (or a login from another tab) is picked up on the next request.
  client.interceptors.request.use((config) => {
    const token = getMerchantAuthToken()
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`)
    } else {
      config.headers.delete('Authorization')
    }
    return config
  })

  return client
}

let instance = createInstance()

export function initMerchantApiClient(reportApiError: (error: AxiosError) => void) {
  instance = createInstance()

  instance.interceptors.response.use(
    (response) => {
      const refreshed = response.headers?.[REFRESHED_TOKEN_HEADER] as string | undefined
      if (refreshed) {
        localStorage.setItem(JWT_LOCAL_STORAGE_KEY, refreshed)
      }
      return response
    },
    (error: AxiosError) => {
      if (reportApiError) reportApiError(error)
      throw error
    },
  )
}

export function getApiClient() {
  return instance
}
